import { ArrowForward } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";

import { PreDefinedConstraints } from "../../types";
import { ChatInput } from "./ChatInput";
import { StatusAlert } from "./StatusAlert";

type QuickQueryProps = {
  queryLimitReached: boolean;
  isAsking: boolean;
  askQuestion: (
    question: string,
    warcEnabled: boolean,
    constraints?: PreDefinedConstraints
  ) => void;
  warcEnabledForTier: boolean;
  sampleQuestions: string[];
  isFreeUser: boolean;
  remainingQueries: number;
};

export const QuickQuery = ({
  queryLimitReached,
  isAsking,
  askQuestion,
  warcEnabledForTier,
  sampleQuestions,
  isFreeUser,
  remainingQueries,
}: QuickQueryProps) => {
  return (
    <Box sx={{ flexShrink: 0 }}>
      {/* Ask question input */}
      <ChatInput
        onAskQuestion={askQuestion}
        isAnswerPending={isAsking}
        warcEnabledForTier={true}
        disabled={queryLimitReached}
        mb={1.5}
      />

      {/* Query limit alerts */}
      {!queryLimitReached && isFreeUser && (
        <StatusAlert
          title={`You have ${remainingQueries} MAITE ${remainingQueries === 1 ? "query" : "queries"} remaining`}
          severity="info"
          sx={{ mb: 2, mt: 0 }}
        >
          Talk to your Marketing Science Partner to unlock full MAITE access. Get unlimited queries,
          chat history, and much more.
        </StatusAlert>
      )}

      {queryLimitReached && (
        <StatusAlert
          title="You've used all your free queries for this month"
          severity="warning"
          sx={{ mb: 2, mt: 0 }}
        >
          Talk to your Marketing Science Partner to unlock full MAITE access. Get unlimited queries,
          chat history, and much more.
        </StatusAlert>
      )}

      <StatusAlert
        severity="info"
        sx={{
          backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#2A3A4B" : "#E5F2F9"),
          mb: 2,
        }}
      >
        <Typography variant="body1" component="div" sx={{ fontWeight: 400 }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            Note:
          </Box>{" "}
          Breakdowns for individual brands, product lines or business lines become less reliable
          when requested together. For the most accurate results, query one brand, product or
          business line at a time.
        </Typography>
      </StatusAlert>

      <Typography variant="body2" sx={{ display: "block", fontWeight: 600, mb: 1 }}>
        What questions can i ask?
      </Typography>

      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {sampleQuestions.map((question, index) => (
          <li
            key={index}
            onClick={() => !queryLimitReached && askQuestion(question, warcEnabledForTier)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: queryLimitReached ? "not-allowed" : "pointer",
              margin: "0 0 8px 0",
              opacity: queryLimitReached ? 0.5 : 1,
            }}
          >
            <ArrowForward sx={{ fontSize: 16, color: "#2E2E2E" }} />
            <span
              style={{
                color: "#2E2E2E",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: 400,
                letterSpacing: "0.4px",
                padding: "4px 12px",
                backgroundColor: "#F6F1FF",
                borderRadius: "4px",
                transition: "background-color 0.2s ease",
                pointerEvents: queryLimitReached ? "none" : "auto",
              }}
              onMouseEnter={(e) => {
                if (!queryLimitReached) {
                  e.currentTarget.style.backgroundColor = "#EADFFF";
                }
              }}
              onMouseLeave={(e) => {
                if (!queryLimitReached) {
                  e.currentTarget.style.backgroundColor = "#F6F1FF";
                }
              }}
            >
              {question}
            </span>
          </li>
        ))}
      </ul>
    </Box>
  );
};
